
import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';

// Types
export interface BookingData {
  id: string;
  name: string;
  email: string;
  businessType: string;
  serviceType: "fixed" | "subscription";
  message: string;
  date: Date;
  timeSlot: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  createdAt: Date;
}

export interface ProjectOrder {
  id: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  serviceType: string;
  serviceTier: string;
  price: number;
  projectDescription: string | null;
  paymentProvider: string;
  paymentStatus: string;
  stripeSessionId: string | null;
  paystackReference: string | null;
  createdAt: Date;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: Date;
  relatedId?: string;
  relatedType?: 'booking' | 'project';
}

interface AdminStoreState {
  bookings: BookingData[];
  projects: ProjectOrder[];
  notifications: Notification[];
  leads: any[];
  wordpressDownloads: any[];
  fetchBookings: () => Promise<void>;
  fetchProjects: () => Promise<void>;
  fetchLeads: () => Promise<void>;
  fetchWordPressDownloads: () => Promise<void>;
  updateBookingStatus: (id: string, status: BookingData['status']) => Promise<void>;
  updateProjectStatus: (id: string, status: string) => Promise<void>;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  markNotificationsAsRead: () => void;
}

// Helper function to convert Supabase booking to app format
const formatBooking = (booking: any): BookingData => ({
  id: booking.id,
  name: booking.name,
  email: booking.email,
  businessType: booking.business_type,
  serviceType: booking.service_type as "fixed" | "subscription",
  message: booking.message,
  date: new Date(booking.date),
  timeSlot: booking.time_slot,
  status: booking.status as "pending" | "confirmed" | "completed" | "cancelled",
  createdAt: new Date(booking.created_at),
});

// Helper function to convert Supabase project to app format
const formatProject = (project: any): ProjectOrder => ({
  id: project.id,
  customerName: project.customer_name,
  customerEmail: project.customer_email,
  customerPhone: project.customer_phone,
  serviceType: project.service_type,
  serviceTier: project.service_tier,
  price: parseFloat(project.price),
  projectDescription: project.project_description,
  paymentProvider: project.payment_provider,
  paymentStatus: project.payment_status,
  stripeSessionId: project.stripe_session_id,
  paystackReference: project.paystack_reference,
  createdAt: new Date(project.created_at),
});

// Create the store
export const useAdminStore = create<AdminStoreState>((set, get) => ({
  bookings: [],
  projects: [],
  notifications: [],
  leads: [],
  wordpressDownloads: [],
  
  fetchBookings: async () => {
    try {
      const { data, error } = await supabase
        .from('consultations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const formattedBookings = data.map(formatBooking);
      set({ bookings: formattedBookings });
      
      // Check for new bookings and notify
      const state = get();
      const existingIds = new Set(state.bookings.map(b => b.id));
      const newBookings = formattedBookings.filter(b => !existingIds.has(b.id) && b.createdAt > new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      newBookings.forEach(booking => {
        state.addNotification({
          title: 'New Consultation Booking',
          message: `${booking.name} has booked a consultation for ${format(booking.date, 'MMM d, yyyy')} at ${booking.timeSlot}`,
          type: 'info',
          relatedId: booking.id,
          relatedType: 'booking'
        });
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  },
  
  fetchProjects: async () => {
    try {
      const { data, error } = await supabase
        .from('project_orders')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const formattedProjects = data.map(formatProject);
      set({ projects: formattedProjects });
      
      // Check for new projects and notify
      const state = get();
      const existingIds = new Set(state.projects.map(p => p.id));
      const newProjects = formattedProjects.filter(p => !existingIds.has(p.id) && p.createdAt > new Date(Date.now() - 24 * 60 * 60 * 1000));
      
      newProjects.forEach(project => {
        state.addNotification({
          title: 'New Project Order',
          message: `${project.customerName} has ordered a ${project.serviceTier} (${project.serviceType}) project for $${project.price}`,
          type: 'success',
          relatedId: project.id,
          relatedType: 'project'
        });
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  },

  fetchLeads: async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ leads: data || [] });
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  },

  fetchWordPressDownloads: async () => {
    try {
      const { data, error } = await supabase
        .from('wordpress_downloads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ wordpressDownloads: data || [] });
    } catch (error) {
      console.error('Error fetching WordPress downloads:', error);
    }
  },
  
  updateBookingStatus: async (id, status) => {
    try {
      const { error } = await supabase
        .from('consultations')
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        bookings: state.bookings.map(booking => 
          booking.id === id ? { ...booking, status } : booking
        )
      }));
      
      const booking = get().bookings.find(b => b.id === id);
      if (booking) {
        get().addNotification({
          title: 'Booking Status Updated',
          message: `${booking.name}'s booking status changed to ${status}`,
          type: 'info',
          relatedId: id,
          relatedType: 'booking'
        });
      }
      
      toast({
        title: "Status Updated",
        description: `Booking status changed to ${status}`,
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast({
        title: "Error",
        description: "Failed to update booking status",
        variant: "destructive",
      });
    }
  },
  
  updateProjectStatus: async (id, status) => {
    try {
      const { error } = await supabase
        .from('project_orders')
        .update({ payment_status: status })
        .eq('id', id);
      
      if (error) throw error;
      
      set(state => ({
        projects: state.projects.map(project => 
          project.id === id ? { ...project, paymentStatus: status } : project
        )
      }));
      
      const project = get().projects.find(p => p.id === id);
      if (project) {
        get().addNotification({
          title: 'Project Status Updated',
          message: `${project.customerName}'s project status changed to ${status}`,
          type: 'info',
          relatedId: id,
          relatedType: 'project'
        });
      }
      
      toast({
        title: "Status Updated",
        description: `Project status changed to ${status}`,
      });
    } catch (error) {
      console.error('Error updating project status:', error);
      toast({
        title: "Error",
        description: "Failed to update project status",
        variant: "destructive",
      });
    }
  },
  
  addNotification: (notification) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      read: false,
    };
    
    set(state => ({
      notifications: [newNotification, ...state.notifications].slice(0, 100) // Keep last 100 notifications
    }));
  },
  
  markNotificationsAsRead: () => {
    set(state => ({
      notifications: state.notifications.map(n => ({ ...n, read: true }))
    }));
  }
}));
